import {
  KLARNA_METHOD,
  TELIAPAY_METHOD,
  type PaymentMethod,
} from '~/models/checkout/PaymentMethods.model';
import { useSortPaymentMethods } from './useSortPaymentMethods';
import { useFetchKlarnaScript } from './useFetchKlarnaScript';
import { useKlarna } from './useKlarna';
import type { KlarnaSessionResponse } from '~/models/checkout/KlarnaSession.model';

export const useFetchPaymentMethods = (isRetail?: boolean) => {
  const { $api } = useNuxtApp();
  const { fetchKlarnaScript } = useFetchKlarnaScript();
  const { fetchAndInitTeliaPay } = useTeliaPayFetchScript();
  const { initKlarna } = useKlarna();
  const notificationStore = useNotificationStore();
  const checkoutStore = useMyCheckoutStore();

  const hasLookedForTeliaPayAndKlarnaInPaymentMethods = ref(false);

  const handleFetchNopPaymentsError = () => {
    notificationStore.setErrorNotificationFromCatchClause(
      'Kunde inte hämta alla betalsätt.',
    );
  };

  const handleFetchKlarnaPaymentsError = () => {
    notificationStore.setErrorNotificationFromCatchClause(
      'Kunde inte hämta Klarnas betalsätt.',
    );
  };

  // prettier-ignore
  const { data: nopPaymentMethods, status: nopPaymentStatus } = 
    useApiDirectly<PaymentMethod[]>(
      () => $api.PaymentModule.getPaymentOptions(), 
      handleFetchNopPaymentsError,
    );

  const {
    data: klarnaPaymentsResponse,
    status: klarnaPaymentStatus,
    trigger: triggerFetchKlarnaMethods,
  } = useApiDirectly<KlarnaSessionResponse>(
    () => $api.klarnaModule.startNewSession(isRetail),
    handleFetchKlarnaPaymentsError,
    { server: false, fetchImmediately: false },
  );

  const klarnaSessionId = computed(
    () => klarnaPaymentsResponse.value?.sessionId,
  );

  const klarnaClientToken = computed(
    () => klarnaPaymentsResponse.value?.clientToken,
  );

  const isLoadingPaymentMethodsForTheFirstTime = computed(
    () =>
      (nopPaymentStatus.value === 'pending' ||
        klarnaPaymentStatus.value === 'pending') &&
      !nopPaymentMethods.value &&
      !klarnaPaymentsResponse.value,
  );

  const paymentMethods = computed(() => {
    const nopMethods = (nopPaymentMethods.value || []).filter(
      (method) => method.systemName !== KLARNA_METHOD,
    );
    const klarnaMethods =
      checkoutStore.ui.klarnaDeniedUserOrAvailability === false &&
      !!klarnaPaymentsResponse.value?.paymentMethods
        ? klarnaPaymentsResponse.value?.paymentMethods
        : [];

    const methods = nopMethods.concat(klarnaMethods);
    const sortedMethods = useSortPaymentMethods(methods);

    return sortedMethods;
  });

  onMounted(async () => {
    if (!import.meta.client) {
      return;
    }

    fetchKlarnaScript();

    watch(
      nopPaymentMethods,
      (methods) => {
        if (!methods || methods.length === 0) {
          return;
        }

        if (hasLookedForTeliaPayAndKlarnaInPaymentMethods.value === true) {
          return;
        }

        hasLookedForTeliaPayAndKlarnaInPaymentMethods.value = true;

        const htmlHasOptimizelyTpOrKlarnaExperimentA =
          document.body.classList.contains('optly_tp-or-klarna_experiment-a');

        const isTeliaPayIncludedInMethods = !!methods.find(
          (method) => method.systemName === TELIAPAY_METHOD,
        );

        const isKlarnaIncludedInMethodsFromNop = !!methods.find(
          (method) =>
            !method.klarnaPaymentMethodCategoryIdentifier &&
            method.systemName.includes('Klarna'),
        );

        if (
          isTeliaPayIncludedInMethods &&
          htmlHasOptimizelyTpOrKlarnaExperimentA
        ) {
          fetchAndInitTeliaPay();
        }

        if (
          isKlarnaIncludedInMethodsFromNop &&
          !htmlHasOptimizelyTpOrKlarnaExperimentA
        ) {
          checkoutStore.ui.loading.isStep4KlarnaMethodsLoading = true;
          triggerFetchKlarnaMethods();
        }
      },
      { immediate: true },
    );
  });

  watch(
    klarnaClientToken,
    (clientToken) => {
      if (clientToken) {
        initKlarna(clientToken);
      }
    },
    { once: true },
  );

  watch(klarnaPaymentStatus, (value, prevValue) => {
    if ((value === 'success' || value === 'error') && prevValue === 'pending') {
      checkoutStore.ui.loading.isStep4KlarnaMethodsLoading = false;
    }
  });

  return {
    paymentMethods,
    isLoadingPaymentMethodsForTheFirstTime,
    klarnaSessionId,
  };
};
